<template>
  <base-data-table
    :service-provider="serviceProvider"
    :vuex-module="vuexModule"
    :header-columns="filteredHeaderColumns"
    :query-params="queryParams"
    label="Users"
    ref="datatable"
  >
    <template #header-left="{ selected: selectedRows }">
      <b-button-group v-if="'Superuser' === $user.get().role" class="mr-3">
        <b-button
          v-for="(assign, assign_i) in filters.assign"
          :key="`assign-${assign_i}`"
          :class="{
            'text-primary': assign.value == queryParams.assigned,
            'text-muted': assign.value != queryParams.assigned
          }"
          @click.prevent="queryParams.assigned = assign.value"
          :disabled="fetching"
          variant="link"
          size="sm"
          class="px-0 mr-2 font-weight-normal shadow-none"
        >{{ assign.text }}</b-button>
      </b-button-group>

      <template v-if="selectedRows && selectedRows.length">
        <b-button
          v-if="'no' == queryParams.assigned"
          @click.stop="
            $bus.$emit('toggle-modal', {
              key: 'assign-company',
              active: true
            })
          "
          variant="link"
          size="sm"
          class="text-info bg-transparent py-1 px-0 font-weight-normal shadow-none"
        >Change Company</b-button>
        <b-button
          v-else
          @click.stop="changeCompany(selectedRows)"
          variant="link"
          size="sm"
          class="text-info bg-transparent py-1 px-0 font-weight-normal shadow-none"
        >Change Company</b-button>
        <span class="d-inline-flex align-self-center mr-2 small">|</span>
        <b-button
          @click.stop.prevent="confirmPromoteSelected(selectedRows)"
          variant="link"
          size="sm"
          class="text-info bg-transparent py-1 px-0 font-weight-normal shadow-none"
        >Promote to Admin</b-button>
        <span class="d-inline-flex align-self-center mr-2 small">|</span>
      </template>
    </template>

    <template #header-right>
      <div class="navbar-search form-inline ml-auto mr-3">
        <b-form-group class="mb-0">
          <b-input-group class="input-group-alternative input-group-merge">
            <div class="input-group-prepend">
              <span class="input-group-text"
                ><i class="fas fa-search"></i
              ></span>
            </div>
            <b-form-input
              @input="onSearch"
              size="sm"
              placeholder="Search"
              type="text"
            ></b-form-input>
          </b-input-group>
        </b-form-group>
      </div>

      <download-csv
        v-if="'Member' !== $user.get().role && usersCSVJson.length"
        :data="usersCSVJson"
        name="Users.csv"
        class="btn btn-outline-primary btn-sm px-4 base-button d-inline-flex"
        >Export to CSV</download-csv
      >
      <base-button
        @click.stop="
          $bus.$emit('toggle-modal', {
            key: 'add-user',
            active: true
          })
        "
        type="primary"
        size="sm"
        class="px-4"
        outline
      >
        <b-icon icon="person-plus" class="mr-1"></b-icon>
        Add User
      </base-button>
    </template>

    <template v-slot:cell_user="{ value, label }">
      <template v-if="'User' === label"
        >{{ value.is_superuser ? '*' : '' }} {{ value.first_name }}</template
      >
      <template v-else-if="'Date Joined' === label">
        <template v-if="value.date_joined">{{
          value.date_joined | moment('timezone', 'UTC', 'MMM DD, YYYY')
        }}</template>
        <template v-else>N/A</template>
      </template>
      <template v-else-if="'Last Login' === label">
        <template v-if="value.last_login">{{
          value.last_login | moment('timezone', 'UTC', 'MMM DD, YYYY')
        }}</template>
        <template v-else>N/A</template>
      </template>
    </template>
    <template v-slot:cell_type="{ value }">
      {{ value }}
    </template>
    <template v-slot:cell_company_detail_OR_company="{ value, row }">
      <template v-if="value">{{ value.name }}</template>
      <a
        v-else
        href
        @click.prevent="
          $bus.$emit('toggle-modal', {
            key: 'assign-company',
            active: true,
            selected: row
          })
        "
        >N/A</a
      >
    </template>
    <template v-slot:cell_status="{ row, value }">
      <template v-if="'Member' == $user.get().role || ('Company Admin' == $user.get().role && 'Company Admin' == row.type)">
        {{ _.capitalize(value) }}
      </template>
      <base-switch
        v-else
        :id="`cash-call-status-${row.id}`"
        :value="'Active' == value"
        onText=""
        offText=""
        @click.prevent="() => handleStatusToggle(row)"
      ></base-switch>
    </template>
    <template v-slot:cell_cash_call_approver="{ row, value }">
      <base-switch
        :id="`cash-call-approver-${row.id}`"
        :value="'Active' == value"
        onText=""
        offText=""
        @click.prevent="() => onSwitchCashCallApproverStatus(row)"
      ></base-switch>
    </template>
    <template v-slot:cell_billing_contact="{ row, value }">
      <base-switch
        :id="`billing-contact-${row.id}`"
        :value="'Active' == value"
        onText=""
        offText=""
        @click.prevent="() => onSwitchBillingStatus(row)"
      ></base-switch>
    </template>
  </base-data-table>
</template>

<script>
import Vue from 'vue'
import { mapMutations } from 'vuex'

import _ from 'lodash'
import { usersTableData } from '@datas'
import { statusSwitchMixin } from '@mixins'
import { MembersService } from '@services'

import Members from '@services/Members'

import BaseDataTable from '@components/BaseDataTable'

export default {
  name: 'table-users',
  mixins: [statusSwitchMixin],
  components: {
    BaseDataTable
  },
  data() {
    return {
      ...usersTableData,
      fetching: false,
      usersCSVJson: []
    }
  },
  computed: {
    filteredHeaderColumns() {
      const company = this.$user.get().company
      const showCashCallApprover = !!(company && company.tpa_internal_approval_required)
      const showBillingContact = !!(company && company.role && company.role.role === 'TPA')

      return this.headerColumns.filter(col => {
        if (col.prop === 'cash_call_approver') return showCashCallApprover
        if (col.prop === 'billing_contact') return showBillingContact
        return true
      })
    }
  },
  methods: {
    ...mapMutations({
      mutateMember: 'UPDATE_MEMBER'
    }),
    onSearch: _.debounce(function (keyword) {
      this.$bus.$emit('datatable-search', keyword)
    }, 500),
    async handleStatusToggle(instance) {
      const me = this.$user.get()
      if (me.role !== 'Superuser' && instance.type === 'Company Admin') {
        this.$toasted && this.$toasted.error('You cannot change the status of another Company Admin.')
        return
      }

      const id = instance.id
      const type =
        'Company Admin' == instance.type ? 'Corporate Admin' : 'Employee'

      Vue.prototype.$swal({
        title: 'Confirmation',
        text: `Are you sure you want to ${
          'Active' == instance.status ? 'deactivate' : 'activate'
        } this ${type}?`,
        type: 'warning',
        confirmButtonText: 'Okay',
        preConfirm: async (confirm) => {
          if (!confirm) return false

          const resp = await Members.toggleMemberStatus(
            id,
            'Active' !== instance.status
          )
          const data = resp.data.data.data

          this.mutateMember({
            data: data
          })

          return true
        }
      })
    },
    async onSwitchCashCallApproverStatus(instance) {
      const id = instance.id

      Vue.prototype.$swal({
        title: 'Confirmation',
        text: `Are you sure you want to ${
          "Active" == instance.cash_call_approver ? "remove" : "designate"
        } ${name} as a Cash Call Approver?`,
        type: 'warning',
        confirmButtonText: 'Okay',
        preConfirm: async (confirm) => {
          if (!confirm) return false

          const resp = await Members.toggleCashCallApproverMemberStatus(
            id,
            'Active' !== instance.cash_call_approver
          )
          const data = resp.data.data.data

          this.mutateMember({
            data: data
          })

          return true
        }
      })
    },
    async onSwitchBillingStatus(instance) {
      Vue.prototype.$swal({
        title: 'Confirmation',
        text: `Are you sure you want to ${
          'Active' == instance.billing_contact ? 'remove' : 'designate'
        } this user as a Billing Contact?`,
        type: 'warning',
        confirmButtonText: 'Okay',
        preConfirm: async (confirm) => {
          if (!confirm) return false

          const resp = await Members.toggleBillingContactStatus(
            instance.id,
            'Active' !== instance.billing_contact
          )

          // Check for error in response directly since axios may not throw
          if (resp.data && resp.data.data && resp.data.data.status === 400) {
            Vue.prototype.$swal({
              title: 'Error',
              text: resp.data.data.message || 'Failed to update billing contact status.',
              type: 'error',
              confirmButtonText: 'Okay'
            })
            return false
          }

          const data = resp.data.data.data
          this.mutateMember({
            data: data
          })

          return true
        }
      })
    },
    async changeCompany(selectedRows) {
      const swal = await this.$swal({
        title: 'Change Company?',
        text: 'This action removes the current company of the selected users.',
        type: 'warning',
        confirmButtonText: 'Okay'
      })

      if (!swal.isConfirmed) return false

      const unassigns = selectedRows || [];

      for (const unassign of unassigns) {
        await MembersService.unassignCompany({
          id: unassign.id,
          display_name: unassign.user.first_name,
          type: unassign.type.replace('Company', 'Corporate'),
          status: 'InActive'
        })

        this.$bus.$emit('datatable-remove-item', unassign)
      }

      this.$toasted.show('Users unassigned succesfully.')
    },
    isAdminUser() {
      const u = this.$user.get && this.$user.get();
      return u && (u.role === 'Superuser' || u.role === 'Company Admin');
    },
    canPromoteSelected(selected) {
      return (selected || []).length > 0 && selected.every(r => r.type === 'Employee');
    },
    async confirmPromoteSelected(selected) {
      if (!selected || !selected.length) return;
      const swal = await this.$swal({
        title: 'Promote to Admin?',
        text: 'Are you sure you want to promote the selected user(s) to Company Admin? This action cannot be easily undone.',
        type: 'warning',
        confirmButtonText: 'Promote'
      });
      if (!swal.isConfirmed) return false;
      for (const user of selected) {
        try {
          await MembersService.update({
            id: user.id,
            type: 'Corporate Admin',
            company: user.company ? user.company.id : null,
            display_name: user.user.first_name,
            status: user.status,
            is_active: user.is_active
          });
        } catch (e) {
          this.$toasted && this.$toasted.error('Failed to promote user: ' + (user.user.email || user.id));
        }
      }
      this.$toasted && this.$toasted.success('Selected users promoted to Company Admin.');
      // Force reload and wait for backend data
      await new Promise((resolve) => {
        this.$bus.$emit('datatable-reload');
        setTimeout(resolve, 500); // Wait for reload to complete (adjust as needed)
      });
    },
  },
  created() {
    this.serviceProvider = MembersService

    if ('Superuser' !== this.$user.get().role) {
      if (this.$user.get().company) {
        this.queryParams.company_id = this.$user.get().company.id
      }
    }
  },
  mounted() {
    this.$bus.trackEvent('datatable-items-filtered', (payload) => {
      this.usersCSVJson = payload.map((item) => {
        return {
          name: `${item.user.is_superuser ? '*' : ''} ${item.user.first_name} ${
            item.user.last_name
          }`,
          email: item.user.email,
          type: item.type,
          company: item.company ? item.company.name : 'N/A',
          date_created: item.user.date_joined,
          last_login: item.user.last_login,
          status: item.user.is_active ? 'Active' : 'Inactive'
        }
      })
    })

    this.$bus.trackEvent('company-role-form-success', (payload) => {
      MembersService.members.results = MembersService.members.results.map(
        (item) => {
          if (payload.id === item.company.id) {
            item.company = payload
            item.role = payload.role.role
            item.role_value = payload.role_value
          }

          return item
        }
      )
    })

    this.$bus.trackEvent('report-access-user-created', (payload) => {
      this.usersCSVJson.push({
        name: `${payload.user.is_superuser ? '*' : ''} ${
          payload.user.first_name
        } ${payload.user.last_name}`,
        email: payload.user.email,
        type: payload.type,
        company: payload.company ? payload.company.name : 'N/A',
        date_created: payload.user.date_joined,
        last_login: payload.user.last_login,
        status: payload.user.is_active ? 'Active' : 'Inactive'
      })
    })

    this.$bus.trackEvent('report-access-unassigned', (payload) => {
      MembersService.members.results
        .filter((mitem) => {
          if ('undefined' !== typeof payload.company_id)
            return (
              'Corporate Admin' === mitem.type ||
              mitem.company.id === payload.company_id
            )
          else return mitem.id === payload.member_id
        })
        .forEach((mitem) => {
          const rindex = mitem.reports.findIndex(
            (ritem) => ritem.report_id === payload.report_id
          )

          if (0 <= rindex) {
            mitem.report_count -= 1
            mitem.reports.splice(rindex, 1)

            MembersService.set('member', mitem)
          }
        })
    })

    this.$bus.trackEvent('report-access-assigned', (payload) => {
      if (0 < payload.length)
        payload.forEach((pitem) => {
          MembersService.members.results.filter((mitem) => {
            if ('undefined' !== typeof pitem.company)
              return 'Corporate Admin' === mitem.type
            else return mitem.user.id === pitem.member.user
          })
        })
    })

    this.$bus.trackEvent('datatable-fetching-data', (payload) => {
      this.fetching = payload
    })

    this.$bus.trackEvent('datatable-item-deleted', (payload) => {
      const idx = this.usersCSVJson.findIndex((item) => item.id === payload.id)

      if (0 <= idx) this.usersCSVJson.splice(idx, 1)
    })

    this.$bus.trackEvent('datatable-cell-click', async (row, column) => {
      MembersService.set('member', row)

      if ('Superuser' == this.$user.get().role) {
        if ('company_detail_OR_company' == column.property) {
          if (row.company) {
            this.$router.push({
              name: 'ManageCompanyProfile',
              params: {
                id:
                  'object' === typeof row.company ? row.company.id : row.company
              }
            })
          }
        }
      }

      if ('user' === column.property && 'User' === column.label)
        this.$bus.$emit('toggle-modal', {
          key: 'user-name',
          active: true
        })
      else if ('type' === column.property)
        this.$bus.$emit('toggle-modal', {
          key: 'user-type',
          active: true
        })
      else if ('report_count' === column.property)
        this.$bus.$emit('toggle-modal', {
          key: 'user-report-access',
          active: true,
          id: row.id
        })
    })

    this.$bus.trackEvent('datatable-selection-changed', (selection) => {
      this.selected = selection
    })
  },
  beforeDestroy() {
    this.$bus.destroyEvents()
  }
}
</script>
